import React, {useMemo} from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
  PDFDownloadLink,
} from '@react-pdf/renderer';
import moment from 'moment';

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Regular.ttf',
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/fonts/Roboto/Roboto-Medium.ttf',
      fontWeight: 'bold',
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontFamily: 'Roboto',
    fontSize: 10,
  },
  header: {
    marginHorizontal: 40,
    marginTop: 20,
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLine: {
    height: 1,
    width: '100%',
    backgroundColor: '#E4E4E6',
    marginBottom: 30,
  },
  titleContainer: {
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0B1C56',
    marginBottom: 10,
  },
  entityTypeBadge: {
    fontSize: 12,
    fontWeight: 'bold',
    borderRadius: 16,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  sectionContainer: {
    flexDirection: 'row',
    marginHorizontal: 18,
    marginBottom: 16,
  },
  sectionHeader: {
    width: '19%',
    backgroundColor: '#FAFAFA',
    padding: 16,
    borderWidth: 1,
    borderColor: '#F4F4F5',
    borderStyle: 'solid',
    borderRadius: 12,
  },
  sectionHeaderText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0B1C56',
  },
  dataTable: {
    width: '81%',
    marginLeft: 4,
    borderWidth: 1,
    borderColor: '#FAFAFA',
    borderStyle: 'solid',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 13,
    overflow: 'hidden',
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#FAFAFA',
    paddingVertical: 3,
    paddingHorizontal: 12,
    height: 35,
    alignItems: 'center',
  },
  dataLabel: {
    width: '30%',
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0B1C56',
  },
  dataValue: {
    width: '70%',
    fontSize: 12,
    color: '#5C688E',
    textAlign: 'right',
  },
  badge: {
    fontSize: 10,
    fontWeight: 'bold',
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 8,
    textAlign: 'center',
    marginLeft: 'auto',
  },
  footer: {
    flexDirection: 'row',
    marginHorizontal: 18,
    marginTop: 30,
    marginBottom: 20,
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 10,
    color: '#858DAA',
    fontWeight: 'bold',
    textAlign: 'center',
    width: '33%',
  },
  logo: {
    width: 45,
    height: 20,
  },
  amlHeaderText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0B1C56',
  },
});

export const AMLReportDocument = ({data, logoImage, userName}) => {
  const {
    search_id,
    match_status,
    risk_level,
    raw_request,
    createdAt,
    created_by,
    monitoring,
    assigned_to,
    total_results,
    total_articles,
    search_query,
    schema_type,
  } = data[0];

  const {properties, screening_options} = raw_request || {};
  const {gender, date_of_birth} = properties || {};
  const match_score = screening_options?.match_threshold;

  const matchStatusOptions = useMemo(
    () => [
      {value: 'No Match', color: '#F6F7F9', textColor: '#344272'},
      {value: 'Partial Match', color: '#D9E5F8', textColor: '#2A5392'},
      {value: 'Potential Match', color: '#E2F5F6', textColor: '#4A878D'},
      {value: 'Confirmed Match', color: '#FCDFDF', textColor: '#A13E3F'},
    ],
    [],
  );

  const riskLevelOptions = useMemo(
    () => [
      {
        value: 'Low level',
        label: 'Low',
        color: '#F0F6EF',
        textColor: '#55A249',
      },
      {
        value: 'Medium level',
        label: 'Medium',
        color: '#FFF8EB',
        textColor: '#DC6803',
      },
      {
        value: 'High level',
        label: 'High',
        color: '#FCDFDF',
        textColor: '#A13E3F',
      },
    ],
    [],
  );

  const formattedDateOfBirth = date_of_birth
    ? moment(date_of_birth).format('DD/MM/YYYY')
    : 'N/A';

  const currentDate = moment().format('DD/MM/YYYY, hh:mmA');
  const formattedMatchScore = match_score
    ? `${Math.round(match_score * 100)}`
    : 'N/A';

  const matchStatus = matchStatusOptions.find(
    option => option.value === match_status,
  ) || {value: match_status || 'N/A', color: '#FAFAFA', textColor: '#5C688E'};

  const riskLevel = riskLevelOptions.find(
    option => option.label === risk_level,
  ) || {
    value: risk_level ? `${risk_level} level` : 'N/A',
    color: '#FAFAFA',
    textColor: '#5C688E',
  };

  const profileData = [
    {label: 'Gender', value: gender || 'N/A'},
    {
      label: 'Date and time created',
      value: createdAt
        ? moment(createdAt).format('Do MMM, YYYY, h:mmA')
        : 'N/A',
    },
    {label: 'Searched by', value: created_by || 'N/A'},
    {label: 'Date of Birth', value: formattedDateOfBirth},
  ];

  const caseData = [
    {label: 'Case ID', value: search_id || 'N/A'},
    {
      label: 'Match status',
      value: matchStatus.value,
      badge: true,
      color: matchStatus.color,
      textColor: matchStatus.textColor,
    },
    {
      label: 'Risk level',
      value: riskLevel.value,
      badge: true,
      color: riskLevel.color,
      textColor: riskLevel.textColor,
    },
    {label: 'Match score', value: formattedMatchScore},
    {
      label: 'Ongoing monitoring',
      value: monitoring !== undefined ? (monitoring ? 'Yes' : 'No') : 'N/A',
    },
    {label: 'Assigned to', value: assigned_to || 'N/A'},
  ];

  const resultData = [
    {label: 'No. of search results', value: total_results || '0'},
    {
      label: 'No. of PEP/Sanction Screening',
      value: total_articles || '0',
    },
  ];

  const DataTable = ({data}) => (
    <View style={styles.dataTable}>
      {data.map((item, index) => (
        <View key={index} style={styles.dataRow}>
          <Text style={styles.dataLabel}>{item.label}</Text>
          {item.badge ? (
            <View style={[styles.badge, {backgroundColor: item.color}]}>
              <Text style={{color: item.textColor, fontSize: 10}}>
                {item.value}
              </Text>
            </View>
          ) : (
            <Text style={styles.dataValue}>{item.value}</Text>
          )}
        </View>
      ))}
    </View>
  );

  const Section = ({title, data}) => (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionHeaderText}>{title}</Text>
      </View>
      <DataTable data={data} />
    </View>
  );

  const capitalizeFirstLetter = str => {
    if (!str) return 'N/A';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          {logoImage && <Image style={styles.logo} src={logoImage} />}
          <Text style={styles.amlHeaderText}>AML Screening</Text>
        </View>

        <View style={styles.headerLine} />

        <View style={styles.titleContainer}>
          <Text style={styles.title}>
            {capitalizeFirstLetter(search_query)}
          </Text>
          <Text
            style={[
              styles.entityTypeBadge,
              {
                backgroundColor:
                  schema_type?.toLowerCase() === 'organization'
                    ? '#E6F7F7'
                    : '#0B1C56',
                color:
                  schema_type?.toLowerCase() === 'organization'
                    ? '#17A6A0'
                    : '#FFFFFF',
              },
            ]}
          >
            {schema_type?.toLowerCase() === 'organization'
              ? 'Organization'
              : 'Individual'}
          </Text>
        </View>

        <Section title="Profile" data={profileData} />
        <Section title="Case Info" data={caseData} />
        <Section title="Result Info" data={resultData} />

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            GENERATED BY: {(userName || 'N/A').toUpperCase()}
          </Text>
          <Text style={styles.footerText}>
            GENERATED: {currentDate.toUpperCase()}
          </Text>
          <Text style={styles.footerText}>
            DOWNLOADED: {currentDate.toUpperCase()}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export const generateAMLReportPDF = (data, userName, fileName, logoImage) => (
  <PDFDownloadLink
    document={
      <AMLReportDocument
        data={data}
        userName={userName}
        logoImage={logoImage}
      />
    }
    fileName={`${fileName}.pdf`.toLowerCase()}
    className="btn-primary"
  >
    {({loading}) => (loading ? 'Preparing document...' : 'Download Report')}
  </PDFDownloadLink>
);
